import { useId, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/stores/authStore';
import { useOrganizationNotes } from '../hooks/useOrganizationNotes';

type Period = 'today' | 'week' | 'month' | 'year' | 'all';

interface UsageChartProps {
  organizationId: string;
  ownerEmail: string;
  memberEmails: string[];
}

const PERIOD_OPTIONS: Array<{ value: Period; label: string; shortLabel: string }> = [
  { value: 'today', label: 'Today', shortLabel: 'Today' },
  { value: 'week', label: 'This Week', shortLabel: 'Week' },
  { value: 'month', label: 'This Month', shortLabel: 'Month' },
  { value: 'year', label: 'This Year', shortLabel: 'Year' },
  { value: 'all', label: 'All Time', shortLabel: 'All' },
];

const startOfPeriod = (period: Period): Date | null => {
  const now = new Date();
  const d = new Date(now);
  switch (period) {
    case 'today':
      d.setHours(0, 0, 0, 0);
      return d;
    case 'week': {
      const day = d.getDay();
      const diff = day === 0 ? 6 : day - 1;
      d.setDate(d.getDate() - diff);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    case 'month':
      return new Date(now.getFullYear(), now.getMonth(), 1);
    case 'year':
      return new Date(now.getFullYear(), 0, 1);
    case 'all':
    default:
      return null;
  }
};

const deriveName = (email: string): string => {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const PALETTE = ['#2B9FFF', '#00CDBC', '#7C4DFF', '#FF7043', '#26A69A', '#EC407A', '#5C6BC0', '#FFB74D'];

const colorForEmail = (email: string): string => {
  let h = 0;
  for (let i = 0; i < email.length; i++) {
    h = (h * 31 + email.charCodeAt(i)) >>> 0;
  }
  return PALETTE[h % PALETTE.length];
};

const formatDateShort = (date: Date): string => date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

interface MemberStat {
  email: string;
  name: string;
  count: number;
  lastDate: Date | null;
  color: string;
}

interface UsageData {
  totalNotes: number;
  memberStats: MemberStat[];
  activeMembers: number;
  totalMembers: number;
  topMember: MemberStat | null;
  sparkline: { date: Date; count: number }[];
  hasAnyData: boolean;
}

const buildSparklineBuckets = (notes: { createdAt: Date }[], period: Period): { date: Date; count: number }[] => {
  const start = startOfPeriod(period);
  const now = new Date();

  const range = (() => {
    if (period === 'today') {
      return { start: start!, end: now, bucketMs: 60 * 60 * 1000, label: 'hour' };
    }
    if (period === 'week') {
      return { start: start!, end: now, bucketMs: 24 * 60 * 60 * 1000, label: 'day' };
    }
    if (period === 'month') {
      return { start: start!, end: now, bucketMs: 24 * 60 * 60 * 1000, label: 'day' };
    }
    if (period === 'year') {
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end: now,
        bucketMs: 30 * 24 * 60 * 60 * 1000,
        label: 'month',
      };
    }
    // all-time: span from first note (or 30 days back) to now
    const earliest = notes.reduce((min, n) => (n.createdAt < min ? n.createdAt : min), notes[0]?.createdAt ?? now);
    const span = now.getTime() - earliest.getTime();
    const targetBuckets = 24;
    const bucketMs = Math.max(24 * 60 * 60 * 1000, span / targetBuckets);
    return { start: earliest, end: now, bucketMs, label: 'period' };
  })();

  const buckets: { date: Date; count: number }[] = [];
  if (period === 'year') {
    for (let m = 0; m <= now.getMonth(); m++) {
      buckets.push({ date: new Date(now.getFullYear(), m, 1), count: 0 });
    }
    notes.forEach((n) => {
      if (n.createdAt.getFullYear() !== now.getFullYear()) return;
      const idx = n.createdAt.getMonth();
      if (buckets[idx]) buckets[idx].count += 1;
    });
    return buckets;
  }

  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  const bucketCount = Math.max(1, Math.ceil((endMs - startMs) / range.bucketMs));
  for (let i = 0; i < bucketCount; i++) {
    buckets.push({ date: new Date(startMs + i * range.bucketMs), count: 0 });
  }
  notes.forEach((n) => {
    const ms = n.createdAt.getTime();
    if (ms < startMs || ms > endMs) return;
    const idx = Math.min(buckets.length - 1, Math.floor((ms - startMs) / range.bucketMs));
    buckets[idx].count += 1;
  });
  return buckets;
};

interface SparklineProps {
  data: { date: Date; count: number }[];
  color: string;
}

const Sparkline = ({ data, color }: SparklineProps) => {
  // SVG element ids are document-global. A hardcoded id meant a second Sparkline
  // on the page would silently reuse the FIRST one's gradient (and its color).
  // Currently masked only by MAX_TEAMS = 1 — don't rely on that.
  const gradientId = useId();
  const width = 600;
  const height = 80;
  const padding = 6;
  const max = Math.max(1, ...data.map((d) => d.count));
  const stepX = data.length > 1 ? (width - padding * 2) / (data.length - 1) : 0;

  const points = data.map((d, i) => ({
    x: padding + i * stepX,
    y: padding + (height - padding * 2) * (1 - d.count / max),
    raw: d,
  }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');

  const areaPath = `${linePath} L ${points[points.length - 1]?.x ?? padding} ${height - padding} L ${
    points[0]?.x ?? padding
  } ${height - padding} Z`;

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {points.length > 1 && (
          <>
            <path d={areaPath} fill={`url(#${gradientId})`} />
            <path
              d={linePath}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={2.5} fill={color} opacity={p.raw.count > 0 ? 1 : 0.2}>
            <title>{`${formatDateShort(p.raw.date)}: ${p.raw.count} recording${p.raw.count !== 1 ? 's' : ''}`}</title>
          </circle>
        ))}
      </svg>
    </Box>
  );
};

interface StatTileProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sublabel?: string;
  color?: string;
}

const StatTile = ({ icon, label, value, sublabel, color }: StatTileProps) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: 2,
      borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: color ?? 'primary.main',
        color: '#fff',
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} noWrap>
        {value}
      </Typography>
      {sublabel && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {sublabel}
        </Typography>
      )}
    </Box>
  </Box>
);

interface MemberBarProps {
  stat: MemberStat;
  maxCount: number;
  totalCount: number;
}

const MemberBar = ({ stat, maxCount, totalCount }: MemberBarProps) => {
  const widthPct = maxCount > 0 ? (stat.count / maxCount) * 100 : 0;
  const sharePct = totalCount > 0 ? Math.round((stat.count / totalCount) * 100) : 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.8rem',
            bgcolor: stat.color,
          }}
        >
          {stat.email.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {stat.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {sharePct}%
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 32, textAlign: 'right' }}>
          {stat.count}
        </Typography>
      </Stack>
      <Tooltip
        title={
          stat.count === 0
            ? 'No recordings in this period'
            : `${stat.count} recording${stat.count !== 1 ? 's' : ''}${
                stat.lastDate ? ` · last on ${formatDateShort(stat.lastDate)}` : ''
              }`
        }
        placement="top"
        arrow
      >
        <Box
          sx={{
            position: 'relative',
            height: 10,
            borderRadius: 5,
            bgcolor: 'action.hover',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'transform 120ms ease',
            '&:hover': { transform: 'translateY(-1px)' },
          }}
        >
          <Box
            component={motion.div}
            initial={{ width: 0 }}
            animate={{ width: `${widthPct}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              borderRadius: 5,
              background: `linear-gradient(90deg, ${stat.color} 0%, ${stat.color}cc 100%)`,
              opacity: stat.count === 0 ? 0.25 : 1,
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

const UsageChart = ({ organizationId, ownerEmail, memberEmails }: UsageChartProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userData } = useAuthStore();
  const [period, setPeriod] = useState<Period>('month');

  const { notes, isLoading } = useOrganizationNotes(organizationId);

  const usageData: UsageData = useMemo(() => {
    const allMembers = Array.from(new Set([ownerEmail, ...memberEmails].filter(Boolean)));
    const blank: MemberStat[] = allMembers.map((email) => ({
      email,
      name: deriveName(email),
      count: 0,
      lastDate: null,
      color: colorForEmail(email),
    }));
    const byEmail = new Map<string, MemberStat>(blank.map((m) => [m.email.toLowerCase(), m]));

    const start = startOfPeriod(period);
    const parsed: { createdAt: Date; email: string }[] = [];

    notes.forEach((note) => {
      if (!note.created_at) return;
      const createdAt = new Date(note.created_at);
      if (Number.isNaN(createdAt.getTime())) return;
      if (start && createdAt < start) return;

      let email: string | null = null;
      if (note.shared_by?.email) {
        email = note.shared_by.email.toLowerCase();
      } else if (userData?.id && note.creator_user_id === userData.id && userData.email) {
        email = userData.email.toLowerCase();
      }
      if (!email) return;

      let stat = byEmail.get(email);
      if (!stat) {
        stat = {
          email,
          name: deriveName(email),
          count: 0,
          lastDate: null,
          color: colorForEmail(email),
        };
        byEmail.set(email, stat);
      }
      stat.count += 1;
      if (!stat.lastDate || createdAt > stat.lastDate) stat.lastDate = createdAt;
      parsed.push({ createdAt, email });
    });

    const memberStats = Array.from(byEmail.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name);
    });

    const totalNotes = parsed.length;
    const activeMembers = memberStats.filter((m) => m.count > 0).length;
    const topMember = memberStats[0]?.count ? memberStats[0] : null;
    const sparkline = buildSparklineBuckets(parsed, period);

    return {
      totalNotes,
      memberStats,
      activeMembers,
      totalMembers: allMembers.length,
      topMember,
      sparkline,
      hasAnyData: notes.length > 0,
    };
  }, [notes, period, ownerEmail, memberEmails, userData?.id, userData?.email]);

  const maxBarCount = Math.max(1, ...usageData.memberStats.map((m) => m.count));
  const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label ?? '';
  const accentColor = theme.palette.primary.main;

  return (
    <Box
      sx={{
        mt: 1,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Team Usage
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Recordings per teammate · {periodLabel.toLowerCase()}
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_, val: Period | null) => val && setPeriod(val)}
          sx={{
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 1.25,
              py: 0.5,
              fontSize: '0.75rem',
              border: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {PERIOD_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} aria-label={opt.label}>
              {isMobile ? opt.shortLabel : opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <StatTile
              icon={<VideocamOutlinedIcon fontSize="small" />}
              label="Total recordings"
              value={String(usageData.totalNotes)}
              sublabel={periodLabel}
              color={theme.palette.primary.main}
            />
            <StatTile
              icon={<EmojiEventsOutlinedIcon fontSize="small" />}
              label="Most active"
              value={usageData.topMember ? usageData.topMember.name : '—'}
              sublabel={
                usageData.topMember
                  ? `${usageData.topMember.count} recording${usageData.topMember.count !== 1 ? 's' : ''}`
                  : 'No recordings yet'
              }
              color={usageData.topMember?.color ?? theme.palette.secondary.main}
            />
            <StatTile
              icon={<GroupsOutlinedIcon fontSize="small" />}
              label="Active members"
              value={`${usageData.activeMembers} / ${usageData.totalMembers}`}
              sublabel="contributed in period"
              color={theme.palette.secondary.main}
            />
          </Stack>

          {usageData.totalNotes > 0 && usageData.sparkline.length > 1 && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Recordings over time
              </Typography>
              <Sparkline data={usageData.sparkline} color={accentColor} />
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              By teammate
            </Typography>
            {usageData.memberStats.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No teammates yet.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {usageData.memberStats.map((stat) => (
                  <MemberBar key={stat.email} stat={stat} maxCount={maxBarCount} totalCount={usageData.totalNotes} />
                ))}
              </Stack>
            )}
          </Box>

          {usageData.totalNotes === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 1 }}>
              No recordings in this period yet — add some notes to see activity here.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default UsageChart;
